import { ExternalLink, Download } from "lucide-react";
import Seo from "../../components/Seo";

const CPN_URL = "https://content.harstatic.com/pdf/TREC_CPN.pdf";

/**
 * TREC Consumer Protection Notice (CN 1-4)
 * Rendered from the official HAR-hosted PDF so it always reflects the
 * current, TREC-verified brokerage information on file for Pierre Joseph
 * / Gens Realty, LLC.
 */
export default function ConsumerProtection() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="consumer-protection-page">
      <Seo
        title="Texas Real Estate Commission Consumer Protection Notice"
        description="Official Texas Real Estate Commission Consumer Protection Notice for Gens Realty, LLC and Pierre Joseph (License #0537207)."
        path="/consumer-protection"
      />

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">
          TREC Form CN 1-4 · Official
        </div>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-[#1A1A1A]">
              Texas Real Estate Commission
              <br />
              <em className="not-italic text-[#E63946]">Consumer Protection Notice</em>
            </h1>
            <p className="text-[#555] mt-4 max-w-2xl">
              Provided by Gens Realty, LLC · Pierre Joseph, Realtor #0537207.
            </p>
          </div>
          <a
            href={CPN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#1A1A1A] hover:bg-[#333] text-white px-5 py-2.5 rounded-md text-sm font-semibold flex items-center gap-2"
            data-testid="cpn-open-official"
          >
            <ExternalLink className="w-4 h-4" /> Open Official PDF
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <iframe
            src={CPN_URL}
            title="TREC Consumer Protection Notice"
            className="w-full h-[1000px] border-0"
            data-testid="cpn-iframe"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
          <a
            href={CPN_URL}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#E63946] hover:underline font-semibold"
            data-testid="cpn-download"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <a href="/iabs" className="text-gray-500 hover:text-[#E63946]">
            View also — Information About Brokerage Services (IABS) →
          </a>
        </div>

        <div className="mt-8 text-[11px] leading-relaxed text-gray-500">
          <p>
            <strong className="text-[#1A1A1A]">Gens Realty, LLC</strong> is a licensed Texas real
            estate brokerage. Broker of record: Pierre Joseph · Texas Realtor License #0537207 ·
            NMLS #1978260 (Edge Home Finance) ·{" "}
            <a href="tel:8327414662" className="underline hover:text-[#E63946]">
              (832) 741-4662
            </a>{" "}
            ·{" "}
            <a href="mailto:PJoseph@gensrealty.com" className="underline hover:text-[#E63946]">
              PJoseph@gensrealty.com
            </a>
            .
          </p>
          <p className="mt-2">
            This notice is provided under Texas Occupations Code § 1101.202 and is required to be
            displayed by every Texas real estate license holder. To file a complaint or verify a
            license, visit{" "}
            <a
              href="https://www.trec.texas.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#E63946]"
            >
              www.trec.texas.gov
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
